import { reaction } from 'mobx';

import { resolveFlag } from '../../services/launchDarkly';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

const CASH_OFFER_DISPLAY_COOLDOWN_MS = 2 * 60 * 1000;
const LAST_SHOWN_STORAGE_KEY = 'cash_offer_last_shown_at';

// Feature Manager participant that auto-surfaces the game-ui dynamic cash offer popup in the lobby.
// Sequencing position comes from the CashOffer key in the
// feature_manager_order LD flag; eligibility is computed here.
export class CashOfferStore implements IFeatureManagerStore {
  public readonly dialogTypes: FeatureManagerDialogType[] = ['CashOffer'];

  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  // Single eligibility rule shared by the lobby widget and the auto-popup (MZ-586):
  // dynamic cash offer flag on, cash-winnings ("ticket states") mode — wsc_available=false,
  // resolved server-side onto the profile — and an actual offer on the wallet payload.
  // The offer is the only balance check needed: player-backend computes it from the live
  // balance and returns null whenever the player isn't eligible.
  public get isEligible(): boolean {
    const isFlagOn = resolveFlag('mini_store_dynamic_cash_offer', false);
    const isCashWinningsMode = this.rootStore.userStore.profile?.geoRestrictionMetadata?.isWscAvailable === false;
    const hasCashOffer = !!this.rootStore.walletStore.balance.cashOffer;
    return isFlagOn && isCashWinningsMode && hasCashOffer;
  }

  private get isOnDisplayCooldown(): boolean {
    const lastShownAt = Number(localStorage.getItem(LAST_SHOWN_STORAGE_KEY));
    return !!lastShownAt && Date.now() - lastShownAt < CASH_OFFER_DISPLAY_COOLDOWN_MS;
  }

  public fetch(): FeatureFetchStatus {
    return this.isEligible ? 'READY' : 'OFF';
  }

  // Always opens — a deliberate widget tap is never cooldown-blocked. It still stamps
  // the timestamp so a fresh manual view delays the next auto-surfacing.
  public openDialog(): void {
    localStorage.setItem(LAST_SHOWN_STORAGE_KEY, String(Date.now()));
    this.rootStore.gameUiStore.openCashOfferDialog();
  }

  // Auto-surfacing (Feature Manager, on login / back-to-lobby) is the only
  // cooldown-gated path.
  public startDialogs(type?: FeatureManagerDialogType): boolean {
    if (!this.isEligible || this.isOnDisplayCooldown) {
      return false;
    }
    this.openDialog();

    // The popup renders in the shared GameUiDialog — advance the sequence once it closes.
    const dispose = reaction(
      () => this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenGameUiDialog,
      (isOpen) => {
        if (!isOpen) {
          dispose();
          this.rootStore.featureManagerStore.onDialogFinished(type ?? 'CashOffer');
        }
      },
    );
    return true;
  }
}
